/**
 * Related Products Carousel Component
 *
 * Initializes Swiper carousel for related products section.
 * Handles responsive visibility — only initializes when the carousel layout
 * is active for the current viewport (desktop/mobile layouts are independent).
 */
export class RelatedProductsCarousel extends HTMLElement {
  swiper = null;
  mutationObserver = null;
  mql = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.onMediaChange = this.onMediaChange.bind(this);
    this.mql.addEventListener('change', this.onMediaChange);

    if (this.querySelector('.swiper-slide')) {
      this.attemptInit();
      return;
    }

    this.mutationObserver = new MutationObserver(() => {
      if (!this.querySelector('.swiper-slide') || this.swiper) {
        return;
      }
      this.attemptInit();
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    });

    this.mutationObserver.observe(this, {childList: true, subtree: true});
  }

  disconnectedCallback() {
    this.mql.removeEventListener('change', this.onMediaChange);
    this.mutationObserver?.disconnect();
    this.destroyCarousel();
  }

  get isVisible() {
    return getComputedStyle(this).display !== 'none';
  }

  attemptInit() {
    if (this.isVisible) {
      this.initCarousel();
    }
  }

  onMediaChange() {
    requestAnimationFrame(() => {
      if (this.isVisible && !this.swiper) {
        this.initCarousel();
      } else if (!this.isVisible && this.swiper) {
        this.destroyCarousel();
      }
    });
  }

  initCarousel() {
    if (!window.Swiper) {
      return;
    }

    const carouselEl = this.querySelector('.related-products__carousel');
    if (!carouselEl) {
      return;
    }
    if (this.swiper || carouselEl.classList.contains('swiper-initialized')) {
      return;
    }

    const wrapper = this.querySelector('.related-products__carousel-wrapper');
    if (!wrapper) {
      return;
    }

    const isQuizMatchResultV2 = Boolean(
      document.querySelector('product-info.template-product-match-result-v2'),
    );

    /**
     * Homepage `index.proposal-2` only: `sections/curated-products.liquid` sets
     * `data-curated-carousel-peek` so peek works even when `body.template-{{ suffix }}`
     * is missing or wrong (e.g. default `index` preview, or empty suffix → `template-`).
     */
    const proposal2IndexPeek =
      this.hasAttribute('data-curated-carousel-peek') ||
      document.body.classList.contains('template-proposal-2');

    const shared = {
      pagination: {
        el: carouselEl.querySelector('.swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: wrapper.querySelector('.swiper-button-next'),
        prevEl: wrapper.querySelector('.swiper-button-prev'),
      },
      watchOverflow: true,
    };

    let swiperOptions;
    if (proposal2IndexPeek) {
      swiperOptions = {
        ...shared,
        slidesPerView: 'auto',
        spaceBetween: 20,
        centeredSlides: false,
        breakpoints: {
          751: {slidesPerView: 2, spaceBetween: 12},
          990: {slidesPerView: 4, spaceBetween: 20},
        },
      };
    } else if (isQuizMatchResultV2) {
      swiperOptions = {
        ...shared,
        slidesPerView: 'auto',
        spaceBetween: 16,
      };
    } else {
      swiperOptions = {
        ...shared,
        slidesPerView: 1,
        spaceBetween: 0,
        breakpoints: {
          480: {slidesPerView: 2, spaceBetween: 12},
          750: {slidesPerView: 3, spaceBetween: 16},
          990: {slidesPerView: 4, spaceBetween: 20},
        },
      };
    }

    this.swiper = new window.Swiper(carouselEl, swiperOptions);
  }

  destroyCarousel() {
    if (!this.swiper) {
      return;
    }
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get('related-products-carousel')) {
  customElements.define('related-products-carousel', RelatedProductsCarousel);
}

